<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { relativeDayTimestamp } from 'shared/helpers/timeHelper';
import { getInboxVoiceIcon } from 'dashboard/helper/inbox';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AudioPlayer from 'dashboard/components-next/audio/AudioPlayer.vue';
import {
  VOICE_CALL_DIRECTION,
  VOICE_CALL_STATUS,
} from 'dashboard/components-next/message/constants';
import CallStatusBadge from './CallStatusBadge.vue';
import { CALL_KIND, getCallKind } from './constants';

const props = defineProps({
  call: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();

const kind = computed(() => getCallKind(props.call));

const contactName = computed(() => {
  if (!props.call.contact) return t('CALLS_PAGE.ROW.DELETED_CONTACT');
  return (
    props.call.contact.name ||
    props.call.contact.phoneNumber ||
    ''
  ).replace(/^\+/, '');
});

const agentActionLabel = computed(() => {
  if (!props.call.agent) return '';
  if (kind.value === CALL_KIND.OUTGOING) return t('CALLS_PAGE.ROW.DIALED_BY');
  if (kind.value === CALL_KIND.INCOMING) return t('CALLS_PAGE.ROW.PICKED_BY');
  // Ongoing collapses direction, so resolve dialed-vs-picked from the raw value.
  if (kind.value === CALL_KIND.ONGOING) {
    return props.call.direction === VOICE_CALL_DIRECTION.OUTBOUND
      ? t('CALLS_PAGE.ROW.DIALED_BY')
      : t('CALLS_PAGE.ROW.PICKED_BY');
  }
  return '';
});

const resultLabel = computed(() => {
  if (kind.value === CALL_KIND.MISSED) return t('CALLS_PAGE.ROW.NO_AGENT');
  if (kind.value === CALL_KIND.NO_REPLY) {
    return t('CALLS_PAGE.ROW.NO_CONTACT_ANSWER');
  }
  if (kind.value === CALL_KIND.FAILED) return t('CALLS_PAGE.ROW.FAILED');
  if (kind.value === CALL_KIND.ONGOING) {
    return props.call.status === VOICE_CALL_STATUS.RINGING
      ? t('CALLS_PAGE.ROW.RINGING')
      : t('CALLS_PAGE.ROW.IN_PROGRESS');
  }
  return t('CALLS_PAGE.ROW.ANSWERED');
});

const providerIcon = computed(() =>
  getInboxVoiceIcon(props.call.inbox.channelType, props.call.inbox.medium)
);

const createdAtLabel = computed(() =>
  relativeDayTimestamp(props.call.createdAt, t('CALLS_PAGE.ROW.YESTERDAY'))
);

const conversationRoute = computed(() => ({
  name: 'inbox_conversation',
  params: {
    accountId: route.params.accountId,
    conversation_id: props.call.conversation.displayId,
  },
  query: { messageId: props.call.messageId },
}));
</script>

<template>
  <article
    class="group bg-auth-surface text-auth-text transition-colors hover:bg-auth-soft/55 dark:bg-auth-surface-dark dark:text-auth-text-dark dark:hover:bg-auth-soft-dark/45"
  >
    <div class="flex flex-col gap-3 p-4 lg:hidden">
      <div class="flex min-w-0 items-center gap-2">
        <Avatar
          :src="call.contact?.avatar"
          :name="contactName"
          :size="24"
          rounded-full
        />
        <span
          v-tooltip.top="{
            content: contactName,
            delay: { show: 500, hide: 0 },
          }"
          class="min-w-0 truncate text-sm font-semibold text-auth-text dark:text-auth-text-dark"
        >
          {{ contactName }}
        </span>
        <CallStatusBadge :kind="kind" class="ms-auto shrink-0" />
        <RouterLink
          :to="conversationRoute"
          class="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border border-auth-border bg-auth-soft px-2 text-label-small text-auth-muted transition-colors hover:border-auth-action/40 hover:text-auth-action focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-auth-action dark:border-auth-border-dark dark:bg-auth-soft-dark dark:text-auth-muted-dark dark:hover:border-auth-accent/40 dark:hover:text-auth-accent dark:focus-visible:outline-auth-accent"
        >
          <Icon icon="i-lucide-message-circle" class="size-3.5" />
          {{ call.conversation.displayId }}
          <Icon icon="i-lucide-arrow-up-right" class="size-3.5" />
        </RouterLink>
      </div>
      <div class="flex min-w-0 items-center gap-1.5">
        <template v-if="agentActionLabel">
          <span
            class="shrink-0 text-label-small text-auth-muted dark:text-auth-muted-dark"
          >
            {{ agentActionLabel }}
          </span>
          <Avatar
            :src="call.agent.avatar"
            :name="call.agent.name"
            :size="20"
            rounded-full
          />
          <span
            class="min-w-0 truncate text-body-main text-auth-text dark:text-auth-text-dark"
          >
            {{ call.agent.name }}
          </span>
        </template>
        <span
          v-else
          class="min-w-0 truncate text-body-main text-auth-muted dark:text-auth-muted-dark"
        >
          {{ resultLabel }}
        </span>
        <span
          class="h-3 w-px shrink-0 bg-auth-border dark:bg-auth-border-dark"
        />
        <Icon
          :icon="providerIcon"
          class="size-4 shrink-0 text-auth-muted dark:text-auth-muted-dark"
        />
        <span
          class="min-w-0 truncate text-body-main text-auth-muted dark:text-auth-muted-dark"
        >
          {{ call.inbox.name }}
        </span>
        <span
          v-if="!call.recordingUrl"
          class="ms-auto shrink-0 tabular-nums text-label-small text-auth-muted dark:text-auth-muted-dark"
        >
          {{ createdAtLabel }}
        </span>
      </div>
      <div
        v-if="call.recordingUrl"
        class="flex min-w-0 items-center justify-between gap-2"
      >
        <AudioPlayer
          :src="call.recordingUrl"
          :fallback-duration="call.durationSeconds || 0"
          class="min-w-0 flex-1 !rounded-lg !bg-auth-soft dark:!bg-auth-soft-dark sm:flex-[0.7]"
        />
        <span
          class="shrink-0 tabular-nums text-label-small text-auth-muted dark:text-auth-muted-dark"
        >
          {{ createdAtLabel }}
        </span>
      </div>
    </div>

    <div class="hidden min-h-16 items-center gap-3 px-4 lg:flex">
      <div class="flex w-52 shrink-0 items-center gap-2.5">
        <Avatar
          :src="call.contact?.avatar"
          :name="contactName"
          :size="24"
          rounded-full
        />
        <span
          v-tooltip.top="{
            content: contactName,
            delay: { show: 500, hide: 0 },
          }"
          class="truncate text-sm font-semibold text-auth-text dark:text-auth-text-dark"
        >
          {{ contactName }}
        </span>
      </div>
      <div class="flex min-w-0 grow items-center gap-2">
        <CallStatusBadge :kind="kind" class="shrink-0" />
        <div v-if="agentActionLabel" class="flex min-w-0 items-center gap-1.5">
          <span
            class="min-w-8 shrink truncate text-label-small text-auth-muted dark:text-auth-muted-dark xl:min-w-14"
          >
            {{ agentActionLabel }}
          </span>
          <span class="flex min-w-16 shrink-[20] items-center gap-1.5">
            <Avatar
              :src="call.agent.avatar"
              :name="call.agent.name"
              :size="20"
              rounded-full
            />
            <span
              v-tooltip.top="{
                content: call.agent.name,
                delay: { show: 500, hide: 0 },
              }"
              class="min-w-0 truncate text-body-main text-auth-text dark:text-auth-text-dark"
            >
              {{ call.agent.name }}
            </span>
          </span>
        </div>
        <span
          v-else-if="resultLabel"
          class="min-w-0 shrink-[20] truncate text-body-main text-auth-muted dark:text-auth-muted-dark"
        >
          {{ resultLabel }}
        </span>
        <AudioPlayer
          v-if="call.recordingUrl"
          :src="call.recordingUrl"
          :fallback-duration="call.durationSeconds || 0"
          class="mx-auto w-auto min-w-44 shrink !rounded-lg !bg-auth-soft dark:!bg-auth-soft-dark"
        />
      </div>
      <div
        v-tooltip.top="{
          content: call.inbox.name,
          delay: { show: 500, hide: 0 },
        }"
        class="flex w-40 min-w-4 shrink-[100] items-center justify-end gap-1"
      >
        <Icon
          :icon="providerIcon"
          class="size-4 shrink-0 text-auth-muted dark:text-auth-muted-dark"
        />
        <span
          class="min-w-0 truncate text-body-main text-auth-muted dark:text-auth-muted-dark"
        >
          {{ call.inbox.name }}
        </span>
      </div>
      <RouterLink
        :to="conversationRoute"
        class="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg border border-auth-border bg-auth-soft px-2 text-label-small text-auth-muted transition-colors hover:border-auth-action/40 hover:text-auth-action focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-auth-action dark:border-auth-border-dark dark:bg-auth-soft-dark dark:text-auth-muted-dark dark:hover:border-auth-accent/40 dark:hover:text-auth-accent dark:focus-visible:outline-auth-accent"
      >
        <Icon icon="i-lucide-message-circle" class="size-3.5" />
        {{ call.conversation.displayId }}
        <Icon icon="i-lucide-arrow-up-right" class="size-3.5" />
      </RouterLink>
      <span
        v-tooltip.top="{
          content: createdAtLabel,
          delay: { show: 500, hide: 0 },
        }"
        class="min-w-16 max-w-20 shrink-0 truncate text-end tabular-nums text-label-small text-auth-muted dark:text-auth-muted-dark"
      >
        {{ createdAtLabel }}
      </span>
    </div>
  </article>
</template>
